import { assertNever } from "../lib/utils";
import React, { useCallback, useState } from "react";
import { Button } from "./Button";
export const FILTER_CHIP_VARIANTS = ["primary", "secondary"];
// `bg-selected` is the NavTabPill active token; hover/active are pinned so the chip stays put.
function selectedButtonProps(variant) {
  switch (variant) {
    case "primary":
      return {
        variant: "primary"
      };
    case "secondary":
      return {
        variant: "ghost",
        className: "bg-selected hover:bg-selected active:bg-selected"
      };
    default:
      return assertNever(variant);
  }
}
/**
 * A single filter chip whose selection is controlled by the caller. Selected
 * chips are `primary` (filled) or `secondary` (lighter background); unselected
 * chips are ghost. Sets `aria-pressed`.
 * @summary Controlled single filter chip.
 */
export function FilterChip({
  label,
  icon,
  isSelected = false,
  variant = "primary",
  tooltip,
  disabled = false,
  onClick
}) {
  return /*#__PURE__*/React.createElement(Button, {
    size: "xs",
    label: label,
    icon: icon,
    tooltip: tooltip,
    disabled: disabled,
    "aria-pressed": isSelected,
    onClick: onClick,
    ...(isSelected ? selectedButtonProps(variant) : {
      variant: "ghost"
    })
  });
}
/**
 * A horizontal row of single-select filter chips for narrowing a list or
 * collection to one category at a time, firing onFilterClick on selection.
 * Use it to let users switch between mutually exclusive views or categories
 * (e.g. "Featured", "Research").
 * @summary Single-select category filter chips.
 */
/**
 * @cc [owner:sfriquet,label:product] single-line-chips
 * Chips MUST stay on a single line. When they do not fit the container's width, the row MUST
 * scroll horizontally instead of wrapping onto a new line.
 */
export function FilterChips({
  filters,
  onFilterClick,
  defaultFilter,
  selectedFilter: controlledSelectedFilter,
  disabledFilters,
  variant = "primary"
}) {
  const [uncontrolledSelectedFilter, setUncontrolledSelectedFilter] = useState(defaultFilter && filters.includes(defaultFilter) ? defaultFilter : null);
  const selectedFilter = controlledSelectedFilter !== undefined ? controlledSelectedFilter : uncontrolledSelectedFilter;
  const handleFilterClick = useCallback(filterName => {
    if (disabledFilters !== null && disabledFilters !== void 0 && disabledFilters.includes(filterName)) {
      return;
    }
    // Avoid unnecessary re-renders by only triggering event if filter has changed.
    if (filterName !== selectedFilter) {
      if (controlledSelectedFilter === undefined) {
        setUncontrolledSelectedFilter(filterName);
      }
      onFilterClick(filterName);
    }
  }, [controlledSelectedFilter, disabledFilters, onFilterClick, selectedFilter]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-row gap-2 overflow-x-auto scrollbar-hide"
  }, filters.map(filterName => {
    var _disabledFilters$incl;
    return /*#__PURE__*/React.createElement(FilterChip, {
      key: filterName,
      label: filterName,
      isSelected: selectedFilter === filterName,
      disabled: (_disabledFilters$incl = disabledFilters === null || disabledFilters === void 0 ? void 0 : disabledFilters.includes(filterName)) !== null && _disabledFilters$incl !== void 0 ? _disabledFilters$incl : false,
      variant: variant,
      onClick: () => handleFilterClick(filterName)
    });
  }));
}
//# sourceMappingURL=FilterChips.js.map